import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ShellHeader from '../components/ShellHeader';
import { useVehicles } from '../context/VehiclesContext';

const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 2,
});

const ACCEPTANCE_FEE = 149;
const OPTION_FEE = 149;

function toPositiveNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }

  return numeric;
}

function FinancePage({ authUser }) {
  const navigate = useNavigate();
  const { vehicles } = useVehicles();
  const [search, setSearch] = useState({
    make: '',
    model: '',
  });
  const [vehiclePrice, setVehiclePrice] = useState('12000');
  const [deposit, setDeposit] = useState('1500');
  const [termMonths, setTermMonths] = useState('60');
  const [apr, setApr] = useState('14.48');

  const sourceVehicles = useMemo(() => {
    const unsoldVehicles = vehicles.filter((vehicle) => !vehicle.isSold);
    return unsoldVehicles.length > 0 ? unsoldVehicles : vehicles;
  }, [vehicles]);

  const availableMakes = useMemo(() => {
    return [...new Set(sourceVehicles.map((vehicle) => vehicle.make).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
  }, [sourceVehicles]);

  const availableModels = useMemo(() => {
    if (!search.make) {
      return [];
    }

    return [
      ...new Set(
        sourceVehicles
          .filter((vehicle) => vehicle.make === search.make)
          .map((vehicle) => vehicle.model)
          .filter(Boolean)
      ),
    ].sort((left, right) => left.localeCompare(right));
  }, [search.make, sourceVehicles]);

  const handleSearchChange = (event) => {
    const { name, value } = event.target;

    setSearch((current) => {
      if (name === 'make' && value !== current.make) {
        return {
          ...current,
          make: value,
          model: '',
        };
      }

      return {
        ...current,
        [name]: value,
      };
    });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();

    if (search.make) {
      params.set('make', search.make);
    }

    if (search.model) {
      params.set('model', search.model);
    }

    navigate(`/search${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const result = useMemo(() => {
    const priceValue = toPositiveNumber(vehiclePrice);
    const depositValue = toPositiveNumber(deposit);
    const termValue = Math.max(1, Math.floor(toPositiveNumber(termMonths)));
    const aprValue = toPositiveNumber(apr);

    const amountToFinance = Math.max(0, priceValue - depositValue);
    const monthlyRate = aprValue / 1200;

    let monthlyPayment = 0;

    if (amountToFinance > 0) {
      if (monthlyRate === 0) {
        monthlyPayment = amountToFinance / termValue;
      } else {
        const factor = (1 + monthlyRate) ** termValue;
        monthlyPayment = amountToFinance * ((monthlyRate * factor) / (factor - 1));
      }
    }

    const totalRepayable = monthlyPayment * termValue;
    const totalInterest = Math.max(0, totalRepayable - amountToFinance);
    const totalFees = ACCEPTANCE_FEE + OPTION_FEE;
    const totalCost = depositValue + totalRepayable + totalFees;

    return {
      amountToFinance,
      monthlyPayment,
      totalRepayable,
      totalInterest,
      acceptanceFee: ACCEPTANCE_FEE,
      optionFee: OPTION_FEE,
      totalFees,
      totalCost,
      termValue,
    };
  }, [vehiclePrice, deposit, termMonths, apr]);

  return (
    <div className="site-shell">
      <ShellHeader authUser={authUser} />

      <main className="content-wrap about-content-wrap finance-content-wrap">
        <section className="content-page-block finance-page-block">
          <div className="section-header">
            <h2>Finance</h2>
          </div>

          <div className="finance-copy-block">
            <p>
              M.J. Motors have a dedicated team of finance experts waiting to help with your enquiry.
            </p>
            <p>
              We have relationships with high street lenders and specialist motoring finance houses too,
              that is why we have helped hundreds of customers secure the best finance deal for their
              budget.
            </p>
            <p>
              Our expertise with finance means that even if you have a poor credit rating we can help
              arrange finance for you.
            </p>
          </div>

          <section className="finance-calculator" aria-label="Rough HP finance calculator">
            <h3 className="finance-calculator-title">Rough HP Finance Calculator</h3>

            <div className="finance-calculator-grid">
              <label className="finance-field">
                <span>Vehicle Price (£)</span>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={vehiclePrice}
                  onChange={(event) => setVehiclePrice(event.target.value)}
                />
              </label>

              <label className="finance-field">
                <span>Deposit (£)</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={deposit}
                  onChange={(event) => setDeposit(event.target.value)}
                />
              </label>

              <label className="finance-field">
                <span>Term (Months)</span>
                <select
                  value={termMonths}
                  onChange={(event) => setTermMonths(event.target.value)}
                >
                  <option value="24">24</option>
                  <option value="36">36</option>
                  <option value="48">48</option>
                  <option value="60">60</option>
                  <option value="72">72</option>
                </select>
              </label>

              <label className="finance-field">
                <span>APR (%)</span>
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  value={apr}
                  onChange={(event) => setApr(event.target.value)}
                />
              </label>
            </div>

            <div className="finance-results" role="status" aria-live="polite">
              <p>
                <span>Amount to Finance</span>
                <strong>{currencyFormatter.format(result.amountToFinance)}</strong>
              </p>
              <p>
                <span>Estimated Monthly Payment</span>
                <strong>{currencyFormatter.format(result.monthlyPayment)}</strong>
              </p>
              <p>
                <span>Total Repayable ({result.termValue} months)</span>
                <strong>{currencyFormatter.format(result.totalRepayable)}</strong>
              </p>
              <p>
                <span>Total Interest</span>
                <strong>{currencyFormatter.format(result.totalInterest)}</strong>
              </p>
              <p>
                <span>Acceptance Fee</span>
                <strong>{currencyFormatter.format(result.acceptanceFee)}</strong>
              </p>
              <p>
                <span>Option Fee</span>
                <strong>{currencyFormatter.format(result.optionFee)}</strong>
              </p>
              <p>
                <span>Total Fees</span>
                <strong>{currencyFormatter.format(result.totalFees)}</strong>
              </p>
              <p>
                <span>Total Cost Including Deposit</span>
                <strong>{currencyFormatter.format(result.totalCost)}</strong>
              </p>
            </div>
          </section>

          <p className="finance-disclaimer">
            This calculator provides a rough guide only. Figures are illustrative and may vary based on
            lender criteria, credit profile, and final underwriting. This estimate includes an
            Acceptance Fee of {currencyFormatter.format(ACCEPTANCE_FEE)} and an Option Fee of {currencyFormatter.format(OPTION_FEE)}.
          </p>
        </section>

        <aside className="about-page-sidebar finance-sidebar" aria-label="Finance tools">
          <section className="content-page-block about-side-panel">
            <h3 className="about-side-title">Search Stock</h3>
            <form className="search-panel about-search-panel" onSubmit={handleSearchSubmit}>
              <label className="search-field">
                <span>Make</span>
                <select name="make" value={search.make} onChange={handleSearchChange}>
                  <option value="">All makes</option>
                  {availableMakes.map((make) => (
                    <option key={make} value={make}>
                      {make}
                    </option>
                  ))}
                </select>
              </label>

              <label className="search-field">
                <span>Model</span>
                <select
                  name="model"
                  value={search.model}
                  onChange={handleSearchChange}
                  disabled={!search.make}
                >
                  <option value="">All models</option>
                  {availableModels.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </label>

              <button type="submit" className="search-submit-btn">Search Stock</button>
            </form>
          </section>
        </aside>
      </main>
    </div>
  );
}

export default FinancePage;
